import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink, Lock, Zap, ShieldAlert, KeyRound, Loader2, X, Copy, Check, BookOpen, Eye } from "lucide-react";
import StatusPill from "./components/StatusPill";
import merchantApi from "../../lib/merchantApi";
import { useMerchantApplication } from "../../context/MerchantApplicationContext";

/**
 * Gateway section — Brief 2 §8 + Brief 3.6.3 + Brief 3.13 (Feb 25 2026).
 *
 * Three states:
 *   1. Locked  — anything before approved.
 *   2. Credentials ready — admin has issued shop keys; merchant gets a
 *      ONE-TIME reveal here. After reveal, the keys are gone — the
 *      merchant must store them. Status auto-promotes approved → live.
 *   3. Live — shows persistent non-secret integration info (base URL,
 *      API docs, environment), plus the back-office launcher.
 *
 * Removed Feb 25 2026: the "Launch gateway" button + the
 * gateway.neptunepay.io URL display. That URL doesn't exist — the only
 * real launch target is the gateway back office.
 */
export default function GatewayPage() {
  const { application, loading, refreshDashboard } = useMerchantApplication();
  const [shopCreds, setShopCreds] = useState({ loading: true, data: null });
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealedCreds, setRevealedCreds] = useState(null);

  // Pull shop credentials status (non-secret previews + base URL + docs).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await merchantApi.get("/merchant/shop-credentials/status");
        if (alive) setShopCreds({ loading: false, data: r.data });
      } catch {
        if (alive) setShopCreds({ loading: false, data: { has_credentials: false } });
      }
    })();
    return () => { alive = false; };
  }, [application?.status]);

  if (loading || !application) {
    return <div data-testid="gateway-loading" className="text-zinc-500 text-[12px] uppercase tracking-[0.22em]">Loading…</div>;
  }
  const isLive = application.status === "live";
  const isApproved = application.status === "approved";
  const credsReadyToReveal = (isApproved || isLive)
    && shopCreds.data?.has_credentials
    && !shopCreds.data?.revealed;
  const credsRevealed = shopCreds.data?.has_credentials && shopCreds.data?.revealed;
  const company = application.company_name || "Your account";

  const onReveal = async () => {
    setRevealOpen(true);
  };

  const confirmReveal = async () => {
    try {
      const { data } = await merchantApi.post("/merchant/shop-credentials/reveal");
      setRevealedCreds(data || null);
      setRevealOpen(false);
      await refreshDashboard?.();
      try {
        const r = await merchantApi.get("/merchant/shop-credentials/status");
        setShopCreds({ loading: false, data: r.data });
      } catch { /* noop */ }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) {
        alert("These credentials have already been revealed once. Contact your account exec for a re-reveal.");
      } else if (status === 404) {
        alert("Credentials aren't ready yet. Your account exec will let you know when they are.");
      } else {
        alert("Couldn't reveal — please try again.");
      }
      setRevealOpen(false);
    }
  };

  return (
    <div data-testid="gateway-page" className="max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Gateway</div>
          <h2 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-tight">
            Gateway
          </h2>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusPill status={application.status} />
          <div className="ref-mono text-[11px] text-zinc-500">{application.ref}</div>
        </div>
      </div>

      {credsReadyToReveal ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          data-testid="gateway-credentials-ready-card"
          className="mt-10 rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.08] via-amber-400/[0.03] to-transparent p-8 sm:p-10"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300">
            <KeyRound className="h-3.5 w-3.5" /> Credentials ready
          </div>
          <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-tight">
            Your shop credentials are ready
          </h3>
          <p className="mt-3 text-[14px] text-zinc-300 leading-relaxed max-w-xl">
            Reveal your Shop API Key + Signing Key below. <strong className="text-amber-200">You&apos;ll only see them once</strong> — save them in a password manager before closing the dialog. The Base URL and API Docs link stay visible here. If you lose the keys, your account exec can re-reveal them.
          </p>
          <button
            type="button"
            onClick={onReveal}
            data-testid="gateway-reveal-credentials-btn"
            className="mt-7 group inline-flex items-center gap-1.5 rounded-full bg-amber-300 text-black hover:bg-amber-200 transition-colors px-5 py-3 text-[14px] font-medium"
          >
            <Eye className="h-3.5 w-3.5" />
            Reveal credentials (one time)
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </motion.div>
      ) : isLive ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          data-testid="gateway-live-card"
          className="mt-10 rounded-3xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/[0.06] via-[#0055FF]/[0.04] to-transparent p-8 sm:p-10"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00E5FF]">
            <Zap className="h-3.5 w-3.5" /> Live
          </div>
          <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-tight">
            {company}
          </h3>
          <p className="mt-3 text-[14px] text-zinc-400 leading-relaxed max-w-lg">
            Your gateway is active. Open the back office to monitor transactions,
            manage settlements and access reporting.
          </p>
          <a
            href={process.env.REACT_APP_BACKOFFICE_URL || "https://backoffice.neptunepay.io"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gateway-backoffice-btn"
            className="mt-7 inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-5 py-3 text-[14px] font-medium"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open gateway back office
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>

          {/* Integration reference — always visible to Live merchants.
              Base URL + API Docs are non-secret. */}
          {shopCreds.data?.has_credentials && (
            <div className="mt-8 grid grid-cols-1 gap-3" data-testid="gateway-integration-ref">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">Integration reference</div>
              {shopCreds.data?.gateway_base_url && (
                <CopyRow label="Base URL" value={shopCreds.data.gateway_base_url} testId="gateway-ref-base-url" />
              )}
              {shopCreds.data?.api_docs_url && (
                <div className="rounded-xl border border-white/[0.06] bg-black/30 px-3 py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">API Docs</div>
                    <a href={shopCreds.data.api_docs_url} target="_blank" rel="noopener noreferrer"
                       data-testid="gateway-ref-api-docs"
                       className="mt-0.5 text-[12.5px] text-cyan-300 hover:text-cyan-200 break-all font-mono inline-flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {shopCreds.data.api_docs_url}
                    </a>
                  </div>
                </div>
              )}
              {/* Show key previews after first reveal — full secrets are
                  gone, the merchant must check their password manager.
                  This block reminds them what they had. */}
              {credsRevealed && (
                <>
                  {shopCreds.data?.api_key_preview && (
                    <div className="rounded-xl border border-white/[0.06] bg-black/30 px-3 py-2.5">
                      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Shop API Key</div>
                      <div className="mt-0.5 text-[12.5px] text-zinc-400 font-mono" data-testid="gateway-ref-api-key-preview">
                        {shopCreds.data.api_key_preview} <span className="text-zinc-600 normal-case">— full value stored in your password manager</span>
                      </div>
                    </div>
                  )}
                  {shopCreds.data?.signing_key_preview && (
                    <div className="rounded-xl border border-white/[0.06] bg-black/30 px-3 py-2.5">
                      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Shop Signing Key</div>
                      <div className="mt-0.5 text-[12.5px] text-zinc-400 font-mono" data-testid="gateway-ref-signing-key-preview">
                        {shopCreds.data.signing_key_preview} <span className="text-zinc-600 normal-case">— full value stored in your password manager</span>
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] text-zinc-500" data-testid="gateway-credentials-revealed-marker">
                    Credentials revealed on {new Date(shopCreds.data.revealed_at).toLocaleString()}.
                    Lost them? Contact your account exec for a re-reveal.
                  </div>
                </>
              )}
            </div>
          )}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          data-testid="gateway-locked-card"
          className="mt-10 rounded-3xl border border-white/[0.08] bg-white/[0.015] p-10 text-center"
        >
          <Lock className="h-6 w-6 text-zinc-500 mx-auto" />
          <h3 className="font-display mt-3 text-xl text-white tracking-tight">Gateway locked</h3>
          <p className="mt-3 text-[13.5px] text-zinc-400 leading-relaxed max-w-md mx-auto">
            Gateway access unlocks once your application is approved and live.
          </p>
        </motion.div>
      )}

      {revealOpen && (
        <RevealConfirmModal
          onClose={() => setRevealOpen(false)}
          onConfirm={confirmReveal}
        />
      )}

      {revealedCreds && (
        <CredentialsRevealedModal
          credentials={revealedCreds}
          onClose={() => setRevealedCreds(null)}
        />
      )}
    </div>
  );
}


function RevealConfirmModal({ onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={() => !submitting && onClose()}
         data-testid="gateway-reveal-confirm-overlay">
      <div className="w-full max-w-md rounded-2xl border border-amber-400/30 bg-[#0a0d12] p-6"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="gateway-reveal-confirm">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-amber-300">
              <ShieldAlert className="h-4 w-4" />
              <div className="text-[10.5px] uppercase tracking-[0.18em]">One-time reveal</div>
            </div>
            <div className="text-white text-[15px] mt-1">Reveal your credentials</div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="gateway-reveal-confirm-close"
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[13.5px] text-zinc-300 leading-relaxed">
          Once revealed, these credentials cannot be shown again. Have a password manager open and ready — copy each value before closing the next dialog.
        </p>
        <p className="text-[13px] text-zinc-500 leading-relaxed mt-3">
          Your account will be marked <strong className="text-zinc-300">Live</strong> as soon as you reveal.
        </p>
        <label className="mt-4 flex items-start gap-2 text-[12.5px] text-zinc-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="gateway-reveal-acknowledge"
            className="mt-1"
          />
          <span>I have a password manager ready and understand this is a one-time reveal.</span>
        </label>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid="gateway-reveal-cancel"
                  className="rounded-full border border-white/[0.08] bg-white/[0.01] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => { setSubmitting(true); await onConfirm(); }}
            disabled={!acknowledged || submitting}
            data-testid="gateway-reveal-confirm-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-300 text-black hover:bg-amber-200 transition-colors px-4 py-2 text-[12.5px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Reveal now
          </button>
        </div>
      </div>
    </div>
  );
}


function CredentialsRevealedModal({ credentials, onClose }) {
  const [copied, setCopied] = useState(null);
  const onCopy = async (key, val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(key);
      setTimeout(() => setCopied(null), 1200);
    } catch { /* noop */ }
  };
  // Render in a fixed order — secrets first, then non-secret reference
  // info. Skip any null values so the dialog stays tight.
  const ORDERED = [
    { key: "api_key", label: "Shop API Key" },
    { key: "signing_key", label: "Shop Signing Key" },
    { key: "gateway_base_url", label: "Base URL" },
    { key: "api_docs_url", label: "API Docs" },
  ].filter((f) => credentials[f.key]);

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="gateway-credentials-revealed-overlay">
      <div className="w-full max-w-lg rounded-2xl border border-amber-400/30 bg-[#0a0d12] p-6"
           onClick={(e) => e.stopPropagation()}
           role="dialog" data-testid="gateway-credentials-revealed">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-amber-300">
              <ShieldAlert className="h-4 w-4" />
              <div className="text-[10.5px] uppercase tracking-[0.18em]">Credentials (one-time view)</div>
            </div>
            <div className="text-white text-[15px] mt-1">Your shop credentials</div>
            <div className="text-zinc-500 text-[12px] mt-1">Copy the API Key and Signing Key now — they won&apos;t appear again unless you ask for a re-reveal.</div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="gateway-credentials-revealed-close"
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        {ORDERED.map(({ key, label }) => (
          <div key={key} className="mb-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12px] text-amber-100 font-mono break-all" data-testid={`gateway-credentials-${key}`}>{credentials[key]}</code>
              <button type="button" onClick={() => onCopy(key, credentials[key])}
                      data-testid={`gateway-credentials-copy-${key}`}
                      className="rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-[11px] text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-200 transition-colors">
                {copied === key ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={onClose}
                data-testid="gateway-credentials-revealed-done"
                className="mt-3 w-full rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-4 py-2.5 text-[13px] font-medium">
          I&apos;ve saved these — close
        </button>
      </div>
    </div>
  );
}


function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* noop */ }
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-black/30 px-3 py-2.5 flex items-center gap-3" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
        <div className="mt-0.5 text-[12.5px] text-zinc-200 font-mono break-all">{value}</div>
      </div>
      <button type="button" onClick={onCopy}
              data-testid={`${testId}-copy`}
              className="shrink-0 rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-[11px] text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-200 transition-colors">
        {copied ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
}
